/**
 * Maintenance page — full-screen lockout when the platform kill-
 * switch is ON. Non-admins hit this the moment `/api/platform/state`
 * reports `is_deactivated: true`; Platform Admins bypass it entirely
 * so they can flip the switch back OFF from Admin → Maintenance.
 *
 * 2026-09-27 (Dave) — v1 spec:
 *   • Structured `start_at` + `back_at` timestamps, rendered in the
 *     user's local timezone (with an abbreviation like "CT" / "PST")
 *   • Optional short note beneath the two timestamps
 *   • Coral-on-navy palette to match the app's brand system
 *   • No login link (session may still be valid — reload lands here
 *     until the switch flips OFF)
 */
import React from "react";
import { Wrench } from "lucide-react";

function _fmtStamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

export default function MaintenancePage({ startAt, backAt, note, serverNow }) {
  // 2026-09-27 (Dave, v2) — the page renders during BOTH `scheduled`
  // (users who hit F5 while banner-dismissed still see the countdown
  // context if fetched directly) and `active` phases. Swap the top-
  // line copy + the "Started/Starts at" label based on whether the
  // window has actually opened.
  const now = serverNow ? new Date(serverNow) : new Date();
  const start = startAt ? new Date(startAt) : null;
  const hasStarted = start ? now >= start : true;
  const headline = hasStarted
    ? "uReserve is Down for Maintenance"
    : "uReserve Maintenance Scheduled";
  const startLabel = hasStarted ? "Started" : "Starts at";
  return (
    <div
      data-testid="maintenance-page"
      className="min-h-screen w-full flex items-center justify-center"
      style={{
        background:
          "linear-gradient(140deg, #0B2545 0%, #13315C 55%, #1C4E80 100%)",
      }}
    >
      <div
        className="mx-4 max-w-xl w-full rounded-[6px] bg-white shadow-2xl"
        style={{ border: "1px solid rgba(11, 37, 69, 0.15)" }}
      >
        <div
          className="flex items-center gap-3 px-6 py-4 rounded-t-[6px]"
          style={{ background: "#EA6A47", color: "#fff" }}
        >
          <Wrench size={22} strokeWidth={2.2} />
          <span className="font-display text-[18px] font-bold tracking-wide">
            {headline}
          </span>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div className="grid grid-cols-[110px_1fr] gap-y-2 text-[14px]">
            <div className="text-slate font-semibold uppercase text-[11px] tracking-[0.09em] pt-[3px]">
              {startLabel}
            </div>
            <div
              data-testid="maintenance-start"
              className="font-mono tabular-nums text-navy"
            >
              {_fmtStamp(startAt)}
            </div>

            <div className="text-slate font-semibold uppercase text-[11px] tracking-[0.09em] pt-[3px]">
              Back at
            </div>
            <div
              data-testid="maintenance-back"
              className="font-mono tabular-nums text-navy font-bold"
            >
              {_fmtStamp(backAt)}
            </div>
          </div>

          {note ? (
            <div
              data-testid="maintenance-note"
              className="text-[13px] text-slate leading-snug pt-3 border-t border-slate/20"
            >
              {note}
            </div>
          ) : null}

          <div className="text-[11px] text-slate/70 pt-2">
            This page will keep showing until the update completes — no need
            to sign back in.
          </div>
        </div>
      </div>
    </div>
  );
}
